"use client";

import { useState, useRef } from "react";
import {
  ImagePlus,
  X,
  Trash2,
  Upload,
  Palette,
  MessageSquare,
  Image as ImageIcon,
  Loader2,
  GripVertical,
} from "lucide-react";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../../firebase";
import toast from "react-hot-toast";

export interface DonationBoxConfig {
  title: string;
  description: string;
  images: string[];
  backgroundImage: string | null;
  primaryColor: string;
  layout: "classic" | "modern" | "minimal";
  imageLayout: "grid" | "carousel";
  thankYouMessage: string;
  buttonText: string;
}

interface DonationBoxEditorProps {
  config: DonationBoxConfig;
  onChange: (config: DonationBoxConfig) => void;
  walletAddress: string;
  onSave: () => void;
  saving: boolean;
  saveLabel?: string;
  storagePrefix?: string;
}

const COLOR_PRESETS = [
  "#EC4899", // Pink
  "#8B5CF6", // Purple
  "#3B82F6", // Blue
  "#06B6D4", // Cyan
  "#10B981", // Green
  "#F59E0B", // Amber
  "#EF4444", // Red
  "#61CE70", // CashTap Green
];

const MAX_IMAGES = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export default function DonationBoxEditor({
  config,
  onChange,
  walletAddress,
  onSave,
  saving,
  saveLabel = "Save Donation Page",
  storagePrefix = "donation-images",
}: DonationBoxEditorProps) {
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);

  const updateField = <K extends keyof DonationBoxConfig>(
    field: K,
    value: DonationBoxConfig[K],
  ) => {
    onChange({ ...config, [field]: value });
  };

  const uploadImage = async (
    file: File,
    path: string,
  ): Promise<string> => {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type });
    return getDownloadURL(storageRef);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remaining = MAX_IMAGES - config.images.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remaining);
    const invalidFiles = filesToUpload.filter(
      (f) => !f.type.startsWith("image/") || f.size > MAX_FILE_SIZE,
    );

    if (invalidFiles.length > 0) {
      toast.error("Some files are invalid. Images must be under 5MB.");
      return;
    }

    setUploadingImages(true);

    try {
      const urls: string[] = [];
      for (const file of filesToUpload) {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
        const path = `${storagePrefix}/${walletAddress}/${timestamp}-${safeName}`;
        const url = await uploadImage(file, path);
        urls.push(url);
      }

      updateField("images", [...config.images, ...urls]);
      toast.success(`${urls.length} image(s) uploaded`);
    } catch (err) {
      console.error("Image upload error:", err);
      toast.error("Failed to upload images");
    } finally {
      setUploadingImages(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const removeImage = async (index: number) => {
    const url = config.images[index];
    try {
      // Try to delete from storage
      const storageRef = ref(storage, url);
      await deleteObject(storageRef).catch(() => {
        // Ignore if file doesn't exist in storage
      });
    } catch {
      // Continue even if storage delete fails
    }
    const newImages = config.images.filter((_, i) => i !== index);
    updateField("images", newImages);
    toast.success("Image removed");
  };

  const handleBgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image must be under 5MB");
      return;
    }

    setUploadingBg(true);

    try {
      const path = `${storagePrefix}/${walletAddress}/bg-${Date.now()}.jpg`;
      const url = await uploadImage(file, path);
      updateField("backgroundImage", url);
      toast.success("Background image uploaded");
    } catch (err) {
      console.error("Background upload error:", err);
      toast.error("Failed to upload background image");
    } finally {
      setUploadingBg(false);
      if (bgInputRef.current) bgInputRef.current.value = "";
    }
  };

  const removeBgImage = () => {
    updateField("backgroundImage", null);
    toast.success("Background image removed");
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <MessageSquare size={16} />
          Title
        </label>
        <input
          type="text"
          value={config.title}
          onChange={(e) => updateField("title", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
          placeholder="Support My Project"
          maxLength={100}
        />
      </div>

      {/* Description */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <MessageSquare size={16} />
          Description
        </label>
        <textarea
          value={config.description}
          onChange={(e) => updateField("description", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all resize-none"
          placeholder="Tell visitors why they should support you..."
          rows={4}
          maxLength={2000}
        />
        <p className="text-gray-500 text-xs mt-1 text-right">
          {config.description.length}/2000
        </p>
      </div>

      {/* Image Gallery Upload */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <ImagePlus size={16} />
          Gallery Images ({config.images.length}/{MAX_IMAGES})
        </label>

        {/* Draggable Image Grid */}
        {config.images.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-3">
            {config.images.map((img, idx) => (
              <div
                key={`${img}-${idx}`}
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverIndex(idx);
                }}
                onDragLeave={() => setDragOverIndex(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null && dragIndex !== idx) {
                    const newImages = [...config.images];
                    const [moved] = newImages.splice(dragIndex, 1);
                    newImages.splice(idx, 0, moved);
                    updateField("images", newImages);
                  }
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
                onDragEnd={() => {
                  setDragIndex(null);
                  setDragOverIndex(null);
                }}
                className={`relative group aspect-square rounded-xl overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all ${
                  dragOverIndex === idx
                    ? "border-[#61CE70] scale-105"
                    : dragIndex === idx
                      ? "border-gray-500 opacity-50"
                      : "border-gray-700/50"
                }`}
              >
                <img
                  src={img}
                  alt={`Upload ${idx + 1}`}
                  className="w-full h-full object-cover pointer-events-none"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="opacity-0 group-hover:opacity-100 p-2 bg-red-500/80 rounded-full text-white hover:bg-red-600 transition-all"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-60">
                  <GripVertical
                    size={16}
                    className="text-white drop-shadow-md"
                  />
                </div>
                {idx === 0 && (
                  <div className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-md font-medium">
                    Cover
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {config.images.length > 1 && (
          <p className="text-gray-500 text-xs mb-2">
            Drag images to reorder. First image is the cover.
          </p>
        )}

        {config.images.length < MAX_IMAGES && (
          <button
            type="button"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImages}
            className="w-full border-2 border-dashed border-gray-700/50 rounded-xl p-6 text-center hover:border-[#61CE70]/50 hover:bg-[#61CE70]/5 transition-all disabled:opacity-50"
          >
            {uploadingImages ? (
              <Loader2
                size={24}
                className="mx-auto text-[#61CE70] animate-spin mb-2"
              />
            ) : (
              <Upload size={24} className="mx-auto text-gray-500 mb-2" />
            )}
            <p className="text-gray-400 text-sm">
              {uploadingImages ? "Uploading..." : "Click to upload images"}
            </p>
            <p className="text-gray-600 text-xs mt-1">
              PNG, JPG up to 5MB each
            </p>
          </button>
        )}

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageUpload}
          className="hidden"
        />
      </div>

      {/* Background Image */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <ImageIcon size={16} />
          Background Image
        </label>

        {config.backgroundImage ? (
          <div className="relative rounded-xl overflow-hidden border border-gray-700/50">
            <img
              src={config.backgroundImage}
              alt="Background"
              className="w-full h-32 object-cover"
            />
            <button
              type="button"
              onClick={removeBgImage}
              className="absolute top-2 right-2 p-2 bg-red-500/80 rounded-full text-white hover:bg-red-600 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => bgInputRef.current?.click()}
            disabled={uploadingBg}
            className="w-full border-2 border-dashed border-gray-700/50 rounded-xl p-4 text-center hover:border-[#61CE70]/50 hover:bg-[#61CE70]/5 transition-all disabled:opacity-50"
          >
            {uploadingBg ? (
              <Loader2
                size={20}
                className="mx-auto text-[#61CE70] animate-spin mb-1"
              />
            ) : (
              <ImageIcon size={20} className="mx-auto text-gray-500 mb-1" />
            )}
            <p className="text-gray-400 text-sm">
              {uploadingBg ? "Uploading..." : "Upload background image"}
            </p>
          </button>
        )}

        <input
          ref={bgInputRef}
          type="file"
          accept="image/*"
          onChange={handleBgUpload}
          className="hidden"
        />
      </div>

      {/* Image Layout */}
      {config.images.length > 1 && (
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
            <ImagePlus size={16} />
            Image Display
          </label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => updateField("imageLayout", "grid")}
              className={`p-3 rounded-xl border text-center transition-all ${
                (config.imageLayout || "grid") === "grid"
                  ? "border-[#61CE70] bg-[#61CE70]/10 text-white"
                  : "border-gray-700/50 bg-gray-800/20 text-gray-400 hover:border-gray-600"
              }`}
            >
              <div className="grid grid-cols-2 gap-1 w-10 h-10 mx-auto mb-2">
                <div className="bg-gray-500/40 rounded-sm" />
                <div className="bg-gray-500/40 rounded-sm" />
                <div className="bg-gray-500/40 rounded-sm" />
                <div className="bg-gray-500/40 rounded-sm" />
              </div>
              <p className="font-medium text-sm">Grid</p>
              <p className="text-xs mt-0.5 opacity-70">Show all at once</p>
            </button>
            <button
              type="button"
              onClick={() => updateField("imageLayout", "carousel")}
              className={`p-3 rounded-xl border text-center transition-all ${
                config.imageLayout === "carousel"
                  ? "border-[#61CE70] bg-[#61CE70]/10 text-white"
                  : "border-gray-700/50 bg-gray-800/20 text-gray-400 hover:border-gray-600"
              }`}
            >
              <div className="flex items-center justify-center gap-1 w-10 h-10 mx-auto mb-2">
                <div className="w-1.5 h-6 bg-gray-600/40 rounded-sm" />
                <div className="w-5 h-8 bg-gray-500/40 rounded-sm" />
                <div className="w-1.5 h-6 bg-gray-600/40 rounded-sm" />
              </div>
              <p className="font-medium text-sm">Carousel</p>
              <p className="text-xs mt-0.5 opacity-70">Auto-scroll slideshow</p>
            </button>
          </div>
        </div>
      )}

      {/* Accent Color */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <Palette size={16} />
          Accent Color
        </label>
        <div className="flex flex-wrap gap-3 items-center">
          {COLOR_PRESETS.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => updateField("primaryColor", color)}
              className={`w-9 h-9 rounded-full border-2 transition-all ${
                config.primaryColor === color
                  ? "border-white scale-110 shadow-lg"
                  : "border-transparent hover:scale-105"
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
          <div className="relative">
            <input
              type="color"
              value={config.primaryColor}
              onChange={(e) => updateField("primaryColor", e.target.value)}
              className="w-9 h-9 rounded-full cursor-pointer border-0 bg-transparent"
              title="Custom color"
            />
          </div>
        </div>
      </div>


      {/* Thank You Message */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <MessageSquare size={16} />
          Thank You Message
        </label>
        <textarea
          value={config.thankYouMessage}
          onChange={(e) => updateField("thankYouMessage", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all resize-none"
          placeholder="Thank you for your generous support!"
          rows={2}
          maxLength={500}
        />
      </div>

      {/* Button Text */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
          <MessageSquare size={16} />
          Button Text
        </label>
        <input
          type="text"
          value={config.buttonText || ""}
          onChange={(e) => updateField("buttonText", e.target.value)}
          className="w-full px-4 py-3 bg-gray-800/30 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:border-[#61CE70] focus:ring-2 focus:ring-[#61CE70]/20 transition-all"
          placeholder="e.g. Help Me , Support Me , Support Sarah , Go Fund Me"
          maxLength={50}
        />
      </div>

      {/* Save Button */}
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="w-full bg-gradient-to-r from-[#61CE70] to-[#50B060] text-white py-3 rounded-xl font-medium hover:shadow-lg hover:shadow-[#61CE70]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 size={18} className="animate-spin" />
            Saving...
          </span>
        ) : (
          saveLabel
        )}
      </button>
    </div>
  );
}
